import * as React from 'react';

export type InputProps = Omit<
  React.ComponentPropsWithRef<'input'>,
  'onChange'
> & {
  autoFocus?: boolean;
  selectOnFocus?: boolean;
  onChange?: (value: string) => void;
};

const SUPPORTS_TOUCH =
  typeof window !== 'undefined' &&
  ('ontouchstart' in window ||
    (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0));

const TextInputBase = React.forwardRef(
  (props: InputProps, ref: React.Ref<HTMLInputElement>) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null);

    const {autoFocus, onChange, selectOnFocus, ...otherProps} = props;
    const handleSelectAll = () => {
      const shouldSelect = selectOnFocus ?? SUPPORTS_TOUCH;
      if (shouldSelect) {
        const input = inputRef.current;
        if (input) {
          input.select();
        }
      }
    };
    React.useEffect(() => {
      if (autoFocus) {
        setTimeout(() => {
          handleSelectAll();
        }, 1);
      }
    }, [autoFocus]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      handleSelectAll();
      props.onFocus && props.onFocus(e);
    };
    const handleRef = (el: HTMLInputElement) => {
      inputRef.current = el;
      if (typeof ref === 'function') {
        ref(el);
      } else if (typeof ref === 'object' && ref !== null) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-type-assertion
        (ref as any).current = el;
      }
    };
    return (
      <input
        type="text"
        {...otherProps}
        autoFocus={autoFocus}
        ref={handleRef}
        onChange={(e) => onChange && onChange(e.target.value)}
        onFocus={handleFocus}
      />
    );
  },
);

export default TextInputBase;
